import './styles.scss'
import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'primereact/calendar'
import Loader from 'Components/Loader'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import { commonMethods } from 'Utils/commonMethods'
import { InputText } from 'primereact/inputtext'
import { Checkbox } from 'primereact/checkbox'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import PreviewTradeInvoice from '../PreviewTradeInvoice'

const AddTradeInvoice = ({ setLoading, tradeInvoiceList, setTradeInvoiceList }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { utcToLocalDate, formatDateToISOWithoutTimezone } = commonMethods
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })

  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [selectedInvoiceDate, setSelectedInvoiceDate] = useState(null)
  const [selectedContractDate, setSelectedContractDate] = useState(null)
  const [countryList, setCountryList] = useState([])
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [validField, setValidField] = useState({
    companyName: false,
    invoiceDate: false,
    buyerStreet: false,
    buyerBuilding: false,
    buyerArea: false,
    buyerCity: false,
    buyerState: false,
    buyerCountry: false,
    buyerPincode: false,
    email: false,
    phoneNumber: false,
    contactPerson: false,
    buyerGstin: false,
    contractNumber: false,
    contractDate: false,
    vehicleNumber: false,
    itemCode: false,
    description: false,
    hsnCode: false,
    quantityInKgs: false,
    ratePerKg: false,
    gstPercentage: false
  })

  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceId: 0,
    invoiceNumber: '',
    invoiceDate: '',
    showSigniture: true,
    buyerCompanyDetails: {
      companyName: '',
      address: {
        buyerStreet: '',
        buyerBuilding: '',
        buyerArea: '',
        buyerCity: '',
        buyerState: '',
        buyerCountry: '',
        buyerPincode: ''
      },
      email: '',
      phoneNumber: '',
      contactPerson: '',
      buyerGstin: ''
    },
    contractDetails: {
      contractNumber: '',
      contractDate: '',
      vehicleNumber: ''
    },
    invoiceItems: [
      {
        invoiceId: 0,
        itemCode: '',
        description: '',
        hsnCode: '',
        quantityInKgs: null,
        ratePerKg: null,
        amount: null,
        gstPercentage: 18,
        gstAmount: null,
        totalWithGst: null
      }
    ],
    totalAmount: null,
    totalAmountWords: '',
    totalGstAmount: null,
    grandTotal: null,
    currency: '',
    applyGst: false,
    declaration: ''
  })

  useEffect(() => {
    fetchCountryList()
  }, [])

  const fetchCountryList = async () => {
    setLoading(true)

    try {
      const response = await apiAdapterCoffeeWeb.getAllCountries()

      const countryOptions = response.data
        ?.map((ele) => ({
          ...ele,
          flag: ele.flagsUrl,
          id: ele.id.toString(),
          name: ele.nicename
        }))
        .sort((a, b) => a.nicename.localeCompare(b.nicename))

      setCountryList(countryOptions)
    } catch {
      setLoading(false)
      setShowDialog({
        showModal: true,
        message: t('FAILED_TO_FETCH_THE_COUNTRY_DATA'),
        modalType: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleChangeInput = (e, itemIndex) => {
    const { name, value } = e.target

    setInvoiceDetails((prev) => {
      const updated = { ...prev }

      if (['invoiceNumber', 'invoiceDate'].includes(name)) {
        updated[name] = value
      } else if (['companyName', 'email', 'phoneNumber', 'contactPerson', 'buyerGstin'].includes(name)) {
        updated.buyerCompanyDetails = { ...prev.buyerCompanyDetails, [name]: value }
      } else if (['buyerStreet', 'buyerBuilding', 'buyerArea', 'buyerCity', 'buyerState', 'buyerCountry', 'buyerPincode'].includes(name)) {
        updated.buyerCompanyDetails = {
          ...prev.buyerCompanyDetails,
          address: {
            ...prev.buyerCompanyDetails.address,
            [name]: value
          }
        }
      } else if (['contractNumber', 'contractDate', 'vehicleNumber'].includes(name)) {
        updated.contractDetails = { ...prev.contractDetails, [name]: value }
      } else if (['description', 'hsnCode', 'quantityInKgs', 'ratePerKg', 'gstPercentage', 'amount'].includes(name)) {
        const items = [...prev.invoiceItems]

        items[itemIndex] = { ...items[itemIndex], [name]: value }

        const amount = items[itemIndex].quantityInKgs * items[itemIndex].ratePerKg
        const gstPercentage = (prev.applyGst && items[itemIndex].gstPercentage) ?? 0
        const gstAmount = +(amount * (gstPercentage / 100)).toFixed(2)
        const totalWithGst = +(amount + gstAmount).toFixed(2)

        items[itemIndex] = { ...items[itemIndex], amount, gstAmount, totalWithGst }
        updated.invoiceItems = items
      } else if (['applyGst'].includes(name)) {
        const items = [...prev.invoiceItems]

        const modifiedItems = items.map((ele) => {
          const gstAmount = ele.quantityInKgs * ele.ratePerKg * ((!prev.applyGst ? ele.gstPercentage : 0) / 100)
          const amount = +(ele.quantityInKgs * ele.ratePerKg)

          return {
            ...ele,
            amount: +(ele.quantityInKgs * ele.ratePerKg),
            gstAmount,
            totalWithGst: amount + gstAmount
          }
        })

        updated.invoiceItems = modifiedItems
        updated[name] = !prev.applyGst
      }

      updated.totalAmount = updated.invoiceItems.reduce((sum, item) => sum + (item.amount ?? 0), 0)
      updated.totalGstAmount = updated.invoiceItems.reduce((sum, item) => sum + (item.gstAmount ?? 0), 0)
      updated.grandTotal = +(updated.totalAmount + updated.totalGstAmount).toFixed(2)

      return updated
    })
  }

  const handleSelectInvoiceDate = (e) => {
    const isoTime = formatDateToISOWithoutTimezone(e.value)
    const isoString = `${isoTime.replace(' ', 'T')}Z`

    setInvoiceDetails({ ...invoiceDetails, invoiceDate: isoString })
    setSelectedInvoiceDate(e.value)
  }

  const handleSelectContractDate = (e) => {
    const isoTime = formatDateToISOWithoutTimezone(e.value)
    const isoString = `${isoTime.replace(' ', 'T')}Z`

    setInvoiceDetails((prev) => ({
      ...prev,
      contractDetails: {
        ...prev.contractDetails,
        contractDate: isoString
      }
    }))

    setSelectedContractDate(e.value)
  }

  const handleClickAddNewItemButton = () => {
    setInvoiceDetails((prev) => ({
      ...prev,
      invoiceItems: [
        ...prev.invoiceItems,
        {
          itemCode: '',
          description: '',
          hsnCode: null,
          quantityInKgs: null,
          ratePerKg: null,
          amount: 0,
          gstPercentage: 18,
          gstAmount: null,
          totalWithGst: null
        }
      ]
    }))
  }
  const handleCountryChange = (event) => {
    const chosenCountry = event.target.value

    setSelectedCountry(chosenCountry)

    setInvoiceDetails((prev) => ({
      ...prev,
      buyerCompanyDetails: {
        ...prev.buyerCompanyDetails,
        address: {
          ...prev.buyerCompanyDetails.address,
          buyerCountry: chosenCountry?.nicename
        }
      }
    }))
  }

  const handleClickRemoveItem = (index) => {
    setInvoiceDetails((prev) => ({
      ...prev,
      invoiceItems: prev.invoiceItems.filter((_, i) => i !== index)
    }))
  }

  const handleClickSaveButton = async () => {
    setLoading(true)
    try {
      const res = await apiAdapterCoffeeWeb.createTradeInvoice(invoiceDetails)

      if (res?.data?.isSuccess) {
        setTradeInvoiceList([...tradeInvoiceList, res.data.returnLst])

        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('TRADE_INVOICE_ADDED_SUCCESSFULLY'),
          onHide: handleSuccess,
          onSuccess: handleSuccess
        })
      } else {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('SOMETHING_WENT_WRONG'),
          onHide: handleDismiss,
          onRetry: handleClickSaveButton
        })
      }
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: error.response.data.message || t('SOMETHING_WENT_WRONG'),
        onHide: handleDismiss,
        onRetry: handleClickSaveButton
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSuccess = () => {
    setShowDialog({ ...showDialog, showModal: false })
    history.goBack()
  }

  const handleClickPreviewButton = () => {
    setShowPreviewModal(true)
  }

  const handleInputBlur = (fieldName) => {
    setValidField((previousData) => ({
      ...previousData,
      [fieldName]: true
    }))
  }

  const handleDismiss = () => setShowDialog({ ...showDialog, showModal: false })

  const PreviewModalFooter = () => (
    <div className="trade-invoice-preview-modal-footer-wrapper">
      <ButtonComponent type={'submit'} onClick={handleClickSaveButton} disabled={false} />
    </div>
  )

  const selectedCountryTemplate = (option, props) => {
    if (option) {
      return (
        <div className={'country-selector-dropdown-template'}>
          {<img alt={option.name} src={option?.flagsUrl} className="selected-country-flag" />}
          <div>{option?.nicename}</div>
        </div>
      )
    }

    return <span>{props.placeholder}</span>
  }

  const countryOptionTemplate = (option) => (
    <div className={'country-selector-panel-template'}>
      {<img alt={option.name} src={option?.flagsUrl} className="option-country-flag" />}
      <div>{option?.nicename}</div>
    </div>
  )

  const isSaveButtonEnabled = false

  const isPreviewEnabled = selectedInvoiceDate && invoiceDetails?.contractDetails?.contractNumber && invoiceDetails?.contractDetails?.vehicleNumber && selectedContractDate && invoiceDetails.buyerCompanyDetails.companyName && invoiceDetails.buyerCompanyDetails.address.buyerBuilding && invoiceDetails.buyerCompanyDetails.address.buyerArea && invoiceDetails.buyerCompanyDetails.address.buyerCity && invoiceDetails.buyerCompanyDetails.address.buyerState && invoiceDetails.buyerCompanyDetails.address.buyerPincode && invoiceDetails.buyerCompanyDetails.email && invoiceDetails.buyerCompanyDetails.phoneNumber && invoiceDetails.buyerCompanyDetails.contactPerson && selectedCountry && invoiceDetails.invoiceItems[0]?.hsnCode && invoiceDetails.invoiceItems[0]?.quantityInKgs && invoiceDetails.invoiceItems[0]?.ratePerKg && (!invoiceDetails.applyGst || invoiceDetails.invoiceItems[0]?.gstPercentage)

  return (
    <div className="add-trade-invoice-component-wrapper">
      <div className="add-trade-invoice-form">
        <div className="form-sub-group">
          <div className="form-sub-title">{t('INVOICE_DETAILS')}</div>
          <div className="input-wrapper invoice-detail-input-wrapper">
            <div>
              <span className="p-float-label">
                <Calendar
                  value={selectedInvoiceDate}
                  name="invoiceDate"
                  onChange={(e) => handleSelectInvoiceDate(e)}
                  onBlur={() => {
                    handleInputBlur('invoiceDate')
                  }}
                  dateFormat="dd M yy"
                  className={`input-fields ${validField.invoiceDate && !selectedInvoiceDate ? 'error' : ''}`}
                />
                <label htmlFor="menuPath" className={`field-label ${validField.invoiceDate && !selectedInvoiceDate ? 'error-label' : ''}`}>
                  <span>{t('INVOICE_DATE')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.invoiceDate && !selectedInvoiceDate && <small className="error-message">{t('INVOICE_DATE_ERROR_MESSAGE')}</small>}
            </div>
          </div>
        </div>
        <div className="form-sub-group">
          <div className="form-sub-title">{t('CONTRACT_DETAILS')}</div>
          <div className="input-wrapper contract-detail-input-wrapper">
            <div>
              <span className="p-float-label">
                <InputText
                  name="contractNumber"
                  onChange={(e) => handleChangeInput(e)}
                  onBlur={() => {
                    handleInputBlur('contractNumber')
                  }}
                  value={invoiceDetails?.contractDetails?.contractNumber}
                  placeholder={t('CONTRACT_NUMBER')}
                  className={`input-fields ${validField.contractNumber && !invoiceDetails?.contractDetails?.contractNumber ? 'error' : ''}`}
                />
                <label htmlFor="contractNumber" className={`field-label ${validField.contractNumber && !invoiceDetails?.contractDetails?.contractNumber ? 'error-label' : ''}`}>
                  <span>{t('CONTRACT_NUMBER')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.contractNumber && !invoiceDetails?.contractDetails?.contractNumber && <small className="error-message">{t('CONTRACT_NUMBER_ERROR_MESSAGE')}</small>}
            </div>
            <div>
              <span className="p-float-label">
                <InputText
                  name="vehicleNumber"
                  onChange={(e) => handleChangeInput(e)}
                  onBlur={() => {
                    handleInputBlur('vehicleNumber')
                  }}
                  value={invoiceDetails?.contractDetails?.vehicleNumber}
                  placeholder={t('VEHICLE_NUMBER')}
                  className={`input-fields ${validField?.vehicleNumber && !invoiceDetails?.contractDetails?.vehicleNumber ? 'error' : ''}`}
                />
                <label htmlFor="vehicleNumber" className={`field-label ${validField?.vehicleNumber && !invoiceDetails?.contractDetails?.vehicleNumber ? 'error-label' : ''}`}>
                  <span>{t('VEHICLE_NUMBER')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField?.vehicleNumber && !invoiceDetails?.contractDetails?.vehicleNumber && <small className="error-message">{t('VEHICLE_NUMBER_ERROR_MESSAGE')}</small>}
            </div>
            <div>
              <span className="p-float-label">
                <Calendar
                  value={selectedContractDate}
                  name="contractDate"
                  onChange={(e) => handleSelectContractDate(e)}
                  onBlur={() => {
                    handleInputBlur('contractDate')
                  }}
                  dateFormat="dd M yy"
                  className={`input-fields ${validField.contractDate && !selectedContractDate ? 'error' : ''}`}
                />
                <label htmlFor="menuPath" className={`field-label ${validField.contractDate && !selectedContractDate ? 'error-label' : ''}`}>
                  <span>{t('CONTRACT_DATE')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.contractDate && !selectedContractDate && <small className="error-message">{t('CONTRACT_DATE_ERROR_MESSAGE')}</small>}
            </div>
          </div>
        </div>

        <div className="form-sub-group">
          <div className="form-sub-title">{t('BUYER_COMPANY_DETAILS')}</div>
          <div className="input-wrapper company-detail-input-wrapper">
            <div>
              <span className="p-float-label">
                <InputText
                  id="companyName"
                  name="companyName"
                  onChange={handleChangeInput}
                  onBlur={() => {
                    handleInputBlur('companyName')
                  }}
                  value={invoiceDetails.buyerCompanyDetails.companyName}
                  placeholder={t('COMPANY_NAME')}
                  className={`input-fields ${validField.companyName && !invoiceDetails.buyerCompanyDetails.companyName ? 'error' : ''}`}
                />
                <label htmlFor="companyName" className={`field-label ${validField.companyName && !invoiceDetails.buyerCompanyDetails.companyName ? 'error-label' : ''}`}>
                  <span>{t('COMPANY_NAME')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.companyName && !invoiceDetails.buyerCompanyDetails.companyName && <small className="error-message">{t('COMPANY_NAME_ERROR_MESSAGE')}</small>}
            </div>

            <div>
              <span className="p-float-label">
                <InputText
                  id="buyerBuilding"
                  name="buyerBuilding"
                  onChange={handleChangeInput}
                  onBlur={() => {
                    handleInputBlur('buyerBuilding')
                  }}
                  value={invoiceDetails.buyerCompanyDetails.address.buyerBuilding}
                  placeholder={t('BUILDING')}
                  className={`input-fields ${validField.buyerBuilding && !invoiceDetails.buyerCompanyDetails.address.buyerBuilding ? 'error' : ''}`}
                />
                <label htmlFor="buyerBuilding" className={`field-label ${validField.buyerBuilding && !invoiceDetails.buyerCompanyDetails.address.buyerBuilding ? 'error-label' : ''}`}>
                  <span>{t('BUILDING')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.buyerBuilding && !invoiceDetails.buyerCompanyDetails.address.buyerBuilding && <small className="error-message">{t('BUILDING_ERROR_MESSAGE')}</small>}
            </div>

            <div>
              <span className="p-float-label">
                <InputText
                  id="buyerArea"
                  name="buyerArea"
                  onChange={handleChangeInput}
                  onBlur={() => {
                    handleInputBlur('buyerArea')
                  }}
                  value={invoiceDetails.buyerCompanyDetails.address.buyerArea}
                  placeholder={t('AREA')}
                  className={`input-fields ${validField.buyerArea && !invoiceDetails.buyerCompanyDetails.address.buyerArea ? 'error' : ''}`}
                />
                <label htmlFor="buyerArea" className={`field-label ${validField.buyerArea && !invoiceDetails.buyerCompanyDetails.address.buyerArea ? 'error-label' : ''}`}>
                  <span>{t('AREA')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.buyerArea && !invoiceDetails.buyerCompanyDetails.address.buyerArea && <small className="error-message">{t('AREA_ERROR_MESSAGE')}</small>}
            </div>

            <div>
              <span className="p-float-label">
                <InputText
                  id="buyerCity"
                  name="buyerCity"
                  onChange={handleChangeInput}
                  onBlur={() => {
                    handleInputBlur('buyerCity')
                  }}
                  value={invoiceDetails.buyerCompanyDetails.address.buyerCity}
                  placeholder={t('CITY')}
                  className={`input-fields ${validField.buyerCity && !invoiceDetails.buyerCompanyDetails.address.buyerCity ? 'error' : ''}`}
                />
                <label htmlFor="buyerCity" className={`field-label ${validField.buyerCity && !invoiceDetails.buyerCompanyDetails.address.buyerCity ? 'error-label' : ''}`}>
                  <span>{t('CITY')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.buyerCity && !invoiceDetails.buyerCompanyDetails.address.buyerCity && <small className="error-message">{t('CITY_ERROR_MESSAGE')}</small>}
            </div>

            <div>
              <span className="p-float-label">
                <InputText
                  id="buyerState"
                  name="buyerState"
                  onChange={handleChangeInput}
                  onBlur={() => {
                    handleInputBlur('buyerState')
                  }}
                  value={invoiceDetails.buyerCompanyDetails.address.buyerState}
                  placeholder={t('STATE')}
                  className={`input-fields ${validField.buyerState && !invoiceDetails.buyerCompanyDetails.address.buyerState ? 'error' : ''}`}
                />
                <label htmlFor="buyerState" className={`field-label ${validField.buyerState && !invoiceDetails.buyerCompanyDetails.address.buyerState ? 'error-label' : ''}`}>
                  <span>{t('STATE')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.buyerState && !invoiceDetails.buyerCompanyDetails.address.buyerState && <small className="error-message">{t('STATE_ERROR_MESSAGE')}</small>}
            </div>
          </div>
          <div className="input-wrapper company-detail-input-wrapper">
            <div>
              <span className="p-float-label">
                <Dropdown
                  id="buyerCountry"
                  name="buyerCountry"
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  onBlur={() => {
                    handleInputBlur('buyerCountry')
                  }}
                  options={countryList}
                  optionLabel="nicename"
                  placeholder={t('COUNTRY')}
                  filter
                  className="country-selector"
                  appendTo={'self'}
                  itemTemplate={countryOptionTemplate}
                  valueTemplate={selectedCountryTemplate}
                  panelClassName="country-selector-panel"
                />

                <label htmlFor="buyerCountry" className={`field-label ${validField.buyerCountry && !selectedCountry ? 'error-label' : ''}`}>
                  <span>{t('COUNTRY')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.buyerCountry && !selectedCountry && <small className="error-message">{t('COUNTRY_ERROR_MESSAGE')}</small>}
            </div>

            <div>
              <span className="p-float-label">
                <InputText
                  id="buyerPincode"
                  name="buyerPincode"
                  onChange={handleChangeInput}
                  onBlur={() => {
                    handleInputBlur('buyerPincode')
                  }}
                  value={invoiceDetails.buyerCompanyDetails.address.buyerPincode}
                  placeholder={t('PINCODE')}
                  className={`input-fields ${validField.buyerPincode && !invoiceDetails.buyerCompanyDetails.address.buyerPincode ? 'error' : ''}`}
                />
                <label htmlFor="buyerPincode" className={`field-label ${validField.buyerPincode && !invoiceDetails.buyerCompanyDetails.address.buyerPincode ? 'error-label' : ''}`}>
                  <span>{t('PIN_CODE')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.buyerPincode && !invoiceDetails.buyerCompanyDetails.address.buyerPincode && <small className="error-message">{t('PIN_CODE_ERROR_MESSAGE')}</small>}
            </div>

            <div>
              <span className="p-float-label">
                <InputText
                  id="email"
                  name="email"
                  onChange={handleChangeInput}
                  onBlur={() => {
                    handleInputBlur('email')
                  }}
                  value={invoiceDetails.buyerCompanyDetails.email}
                  placeholder={t('EMAIL')}
                  className={`input-fields ${validField.email && !invoiceDetails.buyerCompanyDetails.email ? 'error' : ''}`}
                />
                <label htmlFor="email" className={`field-label ${validField.email && !invoiceDetails.buyerCompanyDetails.email ? 'error-label' : ''}`}>
                  <span>{t('EMAIL')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.email && !invoiceDetails.buyerCompanyDetails.email && <small className="error-message">{t('EMAIL_ERROR_MESSAGE')}</small>}
            </div>

            <div>
              <span className="p-float-label">
                <InputText
                  id="phoneNumber"
                  name="phoneNumber"
                  onChange={handleChangeInput}
                  onBlur={() => {
                    handleInputBlur('phoneNumber')
                  }}
                  value={invoiceDetails.buyerCompanyDetails.phoneNumber}
                  placeholder={t('PHONE_NUMBER')}
                  className={`input-fields ${validField.phoneNumber && !invoiceDetails.buyerCompanyDetails.phoneNumber ? 'error' : ''}`}
                />
                <label htmlFor="phoneNumber" className={`field-label ${validField.phoneNumber && !invoiceDetails.buyerCompanyDetails.phoneNumber ? 'error-label' : ''}`}>
                  <span>{t('PHONE_NUMBER')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.phoneNumber && !invoiceDetails.buyerCompanyDetails.phoneNumber && <small className="error-message">{t('PHONE_NUMBER_ERROR_MESSAGE')}</small>}
            </div>
            <div>
              <span className="p-float-label">
                <InputText
                  id="contactPerson"
                  name="contactPerson"
                  onChange={handleChangeInput}
                  onBlur={() => {
                    handleInputBlur('contactPerson')
                  }}
                  value={invoiceDetails.buyerCompanyDetails.contactPerson}
                  placeholder={t('CONTACT_PERSON')}
                  className={`input-fields ${validField.contactPerson && !invoiceDetails.buyerCompanyDetails.contactPerson ? 'error' : ''}`}
                />
                <label htmlFor="contactPerson" className={`field-label ${validField.contactPerson && !invoiceDetails.buyerCompanyDetails.contactPerson ? 'error-label' : ''}`}>
                  <span>{t('CONTACT_PERSON')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.contactPerson && !invoiceDetails.buyerCompanyDetails.contactPerson && <small className="error-message">{t('CONTACT_PERSON_ERROR_MESSAGE')}</small>}
            </div>
          </div>
          <div className="input-wrapper company-detail-input-wrapper">
            <div>
              <span className="p-float-label">
                <InputText
                  id="buyerGstin"
                  name="buyerGstin"
                  onChange={handleChangeInput}
                  onBlur={() => {
                    handleInputBlur('buyerGstin')
                  }}
                  value={invoiceDetails.buyerCompanyDetails.buyerGstin}
                  placeholder={t('GSTIN')}
                  className={`input-fields ${validField.buyerGstin && !invoiceDetails.buyerCompanyDetails.buyerGstin ? 'error' : ''}`}
                />
                <label htmlFor="GSTIN" className={`field-label ${validField.buyerGstin && !invoiceDetails.buyerCompanyDetails.buyerGstin ? 'error-label' : ''}`}>
                  <span>{t('GSTIN')}</span> <span className="mandate">*</span>
                </label>
              </span>
              {validField.buyerGstin && !invoiceDetails.buyerCompanyDetails.buyerGstin && <small className="error-message">{t('GSTIN_ERROR_MESSAGE')}</small>}
            </div>
          </div>
        </div>

        <div className="form-sub-group">
          <div className="form-sub-title">{t('ITEM_DETAILS')}</div>
          <div className={`${invoiceDetails.applyGst ? 'item-detail-labeling-wrapper-with-gst' : 'item-detail-labeling-wrapper-without-gst'}`}>
            <div>
              {t('DeSCRIPTION_OF_GOODS')}
              <span className="mandate">*</span>
            </div>
            <div className="text-right-right">
              {t('HSN_SAN')}
              <span className="mandate">*</span>
            </div>
            <div className="text-right-right">
              {t('QUANTITY_IN_KGS')}
              <span className="mandate">*</span>
            </div>
            <div className="text-right-right">
              {t('RATE_PER_KG')}
              <span className="mandate">*</span>
            </div>
            {invoiceDetails.applyGst && (
              <div className="text-right-right">
                {t('GST_PERCENTAGE')}
                <span className="mandate">*</span>
              </div>
            )}
            <div className="text-right-right">{t('AMOUNT')}</div>
          </div>
          {invoiceDetails.invoiceItems.map((ele, index) => (
            <div key={index} className={`input-wrapper ${invoiceDetails.applyGst ? 'item-details-input-wrapper-with-gst' : 'item-details-input-wrapper-without-gst'}`}>
              <div className="">
                <InputText
                  name="description"
                  onChange={(e) => handleChangeInput(e, index)}
                  onBlur={() => {
                    handleInputBlur('description')
                  }}
                  value={ele?.description}
                  placeholder={t('DeSCRIPTION_OF_GOODS')}
                  className={`input-fields ${validField.description && !ele?.description ? 'error' : ''}`}
                />
                {validField.description && !ele?.description && <small className="error-message">{t('DESCRIPTION_ERROR_MESSAGE')}</small>}
              </div>
              <div className="">
                <InputText
                  name="hsnCode"
                  onChange={(e) => handleChangeInput(e, index)}
                  onBlur={() => {
                    handleInputBlur('hsnCode')
                  }}
                  value={ele.hsnCode}
                  placeholder={t('HSN_SAN')}
                  className={`text-right-right input-fields ${validField.hsnCode && !ele?.hsnCode ? 'error' : ''}`}
                />
                {validField.hsnCode && !ele?.hsnCode && <small className="error-message">{t('HSNCODE_ERROR_MESSAGE')}</small>}
              </div>
              <div className="">
                <InputText
                  name="quantityInKgs"
                  onChange={(e) => handleChangeInput(e, index)}
                  onBlur={() => {
                    handleInputBlur('quantityInKgs')
                  }}
                  value={ele.quantityInKgs}
                  placeholder={t('0')}
                  className={`text-right-right input-fields ${validField.quantityInKgs && !ele?.quantityInKgs ? 'error' : ''}`}
                />
                {validField.quantityInKgs && !ele?.quantityInKgs && <small className="error-message">{t('QUANTITY_IN_KGS_ERROR_MESSAGE')}</small>}
              </div>
              <div className="">
                <InputText
                  name="ratePerKg"
                  onChange={(e) => handleChangeInput(e, index)}
                  onBlur={() => {
                    handleInputBlur('ratePerKg')
                  }}
                  value={ele.ratePerKg}
                  placeholder={t('0.00')}
                  className={`text-right-right input-fields ${validField.ratePerKg && !ele?.ratePerKg ? 'error' : ''}`}
                />
                {validField.ratePerKg && !ele?.ratePerKg && <small className="error-message">{t('RATE_PER_KG_ERROR_MESSAGE')}</small>}
              </div>

              {invoiceDetails.applyGst && (
                <div className="">
                  <InputText
                    name="gstPercentage"
                    onChange={(e) => handleChangeInput(e, index)}
                    onBlur={() => {
                      handleInputBlur('gstPercentage')
                    }}
                    value={ele.gstPercentage}
                    placeholder={t('0')}
                    className={`text-right-right input-fields ${validField.gstPercentage && !ele?.gstPercentage ? 'error' : ''}`}
                  />
                  {validField.gstPercentage && !ele?.gstPercentage && <small className="error-message">{t('GST_PERCETAGE_ERROR_MESSAGE')}</small>}
                </div>
              )}
              <div className="amount-value text-right-right">₹ {(ele.totalWithGst ?? 0).toLocaleString('en-US')}</div>
              <div className="remove-item-trash-icon" onClick={() => handleClickRemoveItem(index)}>
                <span className="pi pi-trash"></span>
              </div>
            </div>
          ))}

          <div className="invoice-items-form-action-buttons">
            <div className="add-new-item-button">
              <ButtonComponent type={'Custom'} label="Add New Item" variant="neutral" onClick={handleClickAddNewItemButton} />
            </div>
            <div className="apply-gst">
              <Checkbox name="applyGst" onChange={(e) => handleChangeInput(e)} checked={invoiceDetails.applyGst}></Checkbox>
              <div>{t('APPLY_GST')}</div>
            </div>
          </div>

          <div className="add-trade-invoice-form-footer">
            <Button className="primary-buttom-style" label={t('PREVIEW')} size="md" onClick={() => handleClickPreviewButton()} disabled={!isPreviewEnabled} />
          </div>
        </div>
      </div>
      <DialogModal {...showDialog} onDismiss={handleDismiss} />

      <Dialog className="trade-invoice-preview-modal-popup" header="Preview Trade Invoice" visible={showPreviewModal} style={{ height: '100vh' }} onHide={() => setShowPreviewModal(false)} footer={PreviewModalFooter}>
        <PreviewTradeInvoice invoiceDetails={invoiceDetails} />
      </Dialog>
    </div>
  )
}

export default Loader(AddTradeInvoice)
